import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { isImage } from 'utils/validate.util'
import teenagerService from '../services/teenager.service'
import { SystemMessages } from 'utils/enums/system-message.enum'
import { useSnackBar } from 'contexts/snackbar.context'
import { useBackdrop } from 'contexts/backdrop.context'
import { HttpErrorType } from 'types/http.type'
import { errorColor, errorMessage } from 'utils/error.util'

export const useTeenagerPictures = (id?: string | number | null) => {
  const [hasChanged, setHasChanged] = useState<boolean>(false)
  const [image, setImage] = useState<File | null | undefined>(null)

  const { showSnackBar } = useSnackBar()
  const { handleBackdropOpen, handleBackdropClose } = useBackdrop()

  const handleChangePicture = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0]
    setHasChanged(false)
    setImage(null)

    if (file && file.size > 1048576) {
      showSnackBar(SystemMessages.MESSAGE_028, 'warning')
      setImage(null)
    } else if (file && isImage(file)) {
      setImage(file)
      setHasChanged(true)
    } else {
      showSnackBar(SystemMessages.MESSAGE_030, 'warning')
      setImage(null)
    }
  }

  const handleResetPicture = async () => {
    setImage(null)
    setHasChanged(false)
    await deletePicture()
  }

  const deletePicture = async () => {
    if (id) {
      handleBackdropOpen()
      await teenagerService
        .deletePicture(id)
        .then(() => {
          showSnackBar(SystemMessages.MESSAGE_001, 'success')
        })
        .catch((error: HttpErrorType) => {
          showSnackBar(errorMessage(error), errorColor(error))
        })
        .finally(() => handleBackdropClose())
    }
  }

  const buscarFoto = useCallback(async () => {
    if (id) {
      await teenagerService
        .getPicture(id)
        .then((response) => {
          if (response.data) {
            setImage(response.data)
          }
        })
        .catch(() => {
          if (!image) setImage(null)
        })
    }
  }, [id])

  useEffect(() => {
    ;(async () => {
      await buscarFoto()
    })()
  }, [buscarFoto])

  const handleSalvar = useCallback(async () => {
    if (id && hasChanged) {
      if (image) {
        handleBackdropOpen()
        await deletePicture()
        await teenagerService
          .savePicture(id, image)
          .then(() => {
            showSnackBar(SystemMessages.MESSAGE_001, 'success')
          })
          .catch((error: HttpErrorType) => {
            showSnackBar(errorMessage(error), errorColor(error))
          })
          .finally(() => handleBackdropClose())
      }
    }
  }, [id, image])

  useEffect(() => {
    ;(async () => {
      await handleSalvar()
    })()
  }, [handleSalvar])

  return {
    image,
    hasChanged,
    setHasChanged,
    handleResetPicture,
    handleChangePicture,
  }
}
